import { ContainerOutlined, FileAddOutlined, PlusOutlined, PrinterOutlined, ToolOutlined } from '@ant-design/icons';
import {
  InventorySelect,
  InventoryUploadDrawer,
  InventoryUploadDrawerHandle,
  ServiceDetailsCard,
  ServiceSelect,
} from '@components';
import { useInventoryCategories } from '@hooks';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Badge, Button, Popover, Tag, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { IOrderServiceSelectProps } from './OrderServiceSelect.props';
import { OrderServicePrintModal } from './OrderServicePrintModal';

const { Text } = Typography;

const sectionStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #f0f7ff 0%, #f8fbff 100%)',
  borderRadius: 10,
  border: '1px solid #d4e4f7',
  padding: '16px 16px 12px',
  marginBottom: 8,
};

const sectionHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 12,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: '#1a3353',
  letterSpacing: 0.2,
};

const iconWrapperStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 6,
  background: '#2CBDFF',
  color: '#fff',
  fontSize: 14,
};

const OrderServiceSelectPresentation: React.FC<IOrderServiceSelectProps> = ({
  atLeastOneService,
  disabled,
  variant = 'selected',
}) => {
  const { serviceStore, authenticationStore, orderStore } = useStore();
  const isTechnician = authenticationStore.currentUser?.role === 'Technician';
  const currentOrderId = orderStore.order?.id;
  const { options: categoryOptions } = useInventoryCategories();
  const uploadDrawerRef = useRef<InventoryUploadDrawerHandle>(null);
  const isAdditional = variant === 'additional';
  const [printModalOpen, setPrintModalOpen] = useState(false);

  const handleUploadSuccess = (createdItems: any[], parsed?: any[]) => {
    serviceStore.addPartsFromInventoryBulk(createdItems, parsed, isAdditional ? 'additional' : 'selected');
  };

  const services = isAdditional
    ? serviceStore.additionalSelectedServices
    : serviceStore.selectedServices;

  const pricingSummary = serviceStore.pricingSummary;

  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

  // Keep active tab in sync: auto-select the newest service when added
  useEffect(() => {
    if (services.length > 0) {
      const lastId = String(services[services.length - 1].id);
      setActiveTab(lastId);
    } else {
      setActiveTab(undefined);
    }
  }, [services.length]);

  const handleSelect = (id: number) => {
    isAdditional ? serviceStore.addAdditionalService(id) : serviceStore.addService(id);
  };

  const handleDeselect = (id: number) => {
    isAdditional
      ? serviceStore.removeAdditionalService(id)
      : serviceStore.removeService(id, atLeastOneService);
  };

  const handleClear = () => {
    isAdditional
      ? serviceStore.clearAdditionalSelectedServices()
      : serviceStore.clearSelectedServices();
  };

  return (
    <div style={sectionStyle}>
      <div style={{ ...sectionHeaderStyle, justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={iconWrapperStyle}>
            <ToolOutlined />
          </div>
          <Text style={sectionTitleStyle}>Services</Text>
          {services.length > 0 && (
            <>
              <Badge
                count={services.length}
                style={{
                  backgroundColor: '#2CBDFF',
                  fontSize: 11,
                  fontWeight: 600,
                  boxShadow: '0 1px 4px rgba(44, 189, 255, 0.3)',
                }}
              />
            </>
          )}
        </div>
        {!isAdditional && services.length > 0 && (
          <div
            style={{
              display: 'flex',
              gap: 16,
              fontSize: 14,
              alignItems: 'center',
              background: '#fff',
              border: '1px solid #d4e4f7',
              borderRadius: 8,
              padding: '8px 16px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            <span style={{ color: '#555' }}>
              Labour:{' '}
              <strong style={{ color: '#1a3353' }}>
                {Utils.formatCurrency(pricingSummary.labourPrice)}
              </strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <span style={{ color: '#555' }}>
              Parts:{' '}
              <strong style={{ color: '#1a3353' }}>
                {Utils.formatCurrency(pricingSummary.partsPrice)}
              </strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <span style={{ color: '#555' }}>
              VAT:{' '}
              <strong style={{ color: '#1a3353' }}>
                {Utils.formatCurrency(pricingSummary.vatAmount)}
              </strong>
            </span>
            <span style={{ color: '#ccc' }}>|</span>
            <Tag
              color="blue"
              style={{ fontSize: 15, fontWeight: 700, padding: '2px 12px', margin: 0 }}
            >
              {Utils.formatCurrency(pricingSummary.totalPrice)}
            </Tag>
          </div>
        )}
      </div>

      <div style={{ marginBottom: services.length > 0 ? 16 : 0 }}>
        <ServiceSelect
          mode="multiple"
          showMyServicesOnly
          placement="topLeft"
          allowClear={!atLeastOneService}
          value={services.map((x) => x.id)}
          defaultValue={services.map((x) => ({
            label: x.normalizedName || x.name,
            value: x.id,
          }))}
          onClear={handleClear}
          onSelect={handleSelect}
          onDeselect={handleDeselect}
          disabled={disabled}
        />
      </div>

      {services.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: -1 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {services.map((s) => {
                const isActive = activeTab === String(s.id);
                const partsMap = isAdditional
                  ? serviceStore.additionalServiceParts
                  : serviceStore.selectedServiceParts;
                const sParts = partsMap.get(s.id) || [];
                const sSubTotal = Utils.calculateLabourPrice(s);
                const sVatAmount = s.isVat
                  ? Utils.roundToTwoDecimals((sSubTotal * serviceStore.tenantVat) / 100)
                  : 0;
                const sPartsPrice = Utils.calculatePartsPrice(sParts);
                const sPartsVat = sParts.reduce(
                  (acc, p) =>
                    acc +
                    Utils.calculateVat({ ...p, duration: p.quantity, vat: serviceStore.tenantVat }),
                  0
                );
                const tabTotal = Utils.roundToTwoDecimals(
                  sSubTotal + sVatAmount + sPartsPrice + sPartsVat
                );
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveTab(String(s.id))}
                    style={{
                      padding: '8px 18px',
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#fff' : '#1a3353',
                      background: isActive ? '#2CBDFF' : '#fff',
                      border: isActive ? '1px solid #2CBDFF' : '1px solid #d4e4f7',
                      borderBottom: isActive ? '1px solid #fff' : '1px solid #d4e4f7',
                      borderRadius: '8px 8px 0 0',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative',
                      zIndex: isActive ? 1 : 0,
                      whiteSpace: 'normal',
                      lineHeight: 1.3,
                      textAlign: 'center',
                      minWidth: 120,
                    }}
                  >
                    <ToolOutlined style={{ marginRight: 6, fontSize: 12 }} />
                    {s.normalizedName || s.name}
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11,
                        fontWeight: 700,
                        marginTop: 2,
                        color: isActive ? 'rgba(255,255,255,0.85)' : '#096dd9',
                      }}
                    >
                      {Utils.formatCurrency(tabTotal)}
                      {s.isVat && (
                        <span
                          style={{
                            marginLeft: 4,
                            fontSize: 9,
                            fontWeight: 600,
                            opacity: 0.75,
                          }}
                        >
                          INC VAT
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ flex: 1 }} />
            {!disabled && activeTab && (
              <div style={{ display: 'flex', gap: 8, paddingBottom: 4 }}>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const id = Number(activeTab);
                    isAdditional
                      ? serviceStore.dispatchAdditionalServiceParts({
                          type: 'ADD_SERVICE_PART',
                          serviceId: id,
                        })
                      : serviceStore.dispatchSelectedServiceParts({
                          type: 'ADD_SERVICE_PART',
                          serviceId: id,
                        });
                  }}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
                >

                  Add extra
                </Button>
                <Popover
                  trigger="click"
                  placement="topRight"
                  content={
                    <div style={{ width: 650 }}>
                      <InventorySelect
                        onSelect={(item) => {
                          const id = Number(activeTab);
                          const dispatch = isAdditional
                            ? serviceStore.dispatchAdditionalServiceParts
                            : serviceStore.dispatchSelectedServiceParts;
                          dispatch({
                            type: 'ADD_SERVICE_PART_FROM_INVENTORY',
                            serviceId: id,
                            servicePart: {
                              name: item.name,
                              number: item.partNumber,
                              reference: item.reference,
                              costPrice: item.costPrice,
                              price: item.sellPrice,
                              quantity: 1,
                              margin:
                                item.sellPrice > 0
                                  ? ((item.sellPrice - item.costPrice) / item.sellPrice) * 100
                                  : 0,
                              type: item.categoryName || 'Part',
                              inventoryId: item.id,
                              availableStock: item.quantity,
                              required: false,
                            },
                          });
                        }}
                      />
                    </div>
                  }
                >
                  <Button
                    icon={<ContainerOutlined />}
                    style={{ backgroundColor: '#722ed1', borderColor: '#722ed1', color: '#fff' }}
                  >
                    From Inventory
                  </Button>
                </Popover>
                {!isTechnician && (
                  <Button
                    icon={<FileAddOutlined />}
                    onClick={() =>
                      uploadDrawerRef.current?.open({
                        orderId: currentOrderId,
                        serviceOptions: services.map((s) => ({
                          id: s.id,
                          name: s.normalizedName || s.name,
                        })),
                      })
                    }
                    style={{ backgroundColor: '#13c2c2', borderColor: '#13c2c2', color: '#fff' }}
                  >
                    Upload parts
                  </Button>
                )}
                <Button
                  icon={<PrinterOutlined />}
                  onClick={() => setPrintModalOpen(true)}
                  style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16', color: '#fff' }}
                >
                  Print
                </Button>
              </div>
            )}
          </div>
          <div
            style={{
              border: '1px solid #d4e4f7',
              borderRadius: '0 8px 8px 8px',
              background: '#fff',
              padding: 4,
            }}
          >
            {services
              .filter((s) => String(s.id) === activeTab)
              .map((s) => (
                <ServiceDetailsCard
                  key={s.id}
                  service={s}
                  disabled={disabled}
                  variant={variant}
                />
              ))}
          </div>
        </>
      )}
      <InventoryUploadDrawer
        ref={uploadDrawerRef}
        categoryOptions={categoryOptions}
        onSuccess={handleUploadSuccess}
      />
      <OrderServicePrintModal
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        services={services}
        partsMap={
          isAdditional ? serviceStore.additionalServiceParts : serviceStore.selectedServiceParts
        }
        tenantVat={serviceStore.tenantVat}
        orderTitle={orderStore.order?.title}
        registrationNumber={orderStore.order?.registrationNumber}
        customerName={orderStore.order?.customer?.fullname}
        mileage={orderStore.order?.mileage}
      />
    </div>
  );
};

export default observer(OrderServiceSelectPresentation);
